import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Loader2, Sparkles, X, CheckCircle2, AlertCircle, ListTodo, Plus, UserCheck } from "lucide-react";
import VoiceApprovalPanel from "@/components/VoiceApprovalPanel";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * VoiceRecorderWidget — floating microphone button (bottom-left, above AI assistant).
 *
 * Flow:
 *   1. User taps button → MediaRecorder starts
 *   2. User taps again to stop → audio uploaded to /api/voice-notes/upload
 *   3. Backend transcribes via Whisper + analyses via Claude Haiku 4.5
 *   4. Tasks extracted are auto-inserted into the user's CRM tasks (diary)
 *   5. We show a success card with task count + "Open in diary" CTA
 *
 * Monthly limit: 10 recordings for advisors, 200 for admin (server-enforced).
 */
export default function VoiceRecorderWidget({ forcedContactId = null, forcedContactName = "", inline = false, onComplete = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [contractDraft, setContractDraft] = useState(null);
  const [savingContract, setSavingContract] = useState(false);
  const [hiddenOnScroll, setHiddenOnScroll] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
    }
  }, []);

  // Mobile: hide floating button when scrolling DOWN, show when scrolling UP.
  // Only on small screens where it overlaps content; ignored when inline (rendered in a panel).
  useEffect(() => {
    if (inline) return;
    const onScroll = () => {
      if (window.innerWidth >= 640) {
        setHiddenOnScroll(false);
        return;
      }
      const y = window.scrollY;
      const dy = y - lastScrollY.current;
      if (Math.abs(dy) < 8) return;
      setHiddenOnScroll(dy > 0 && y > 80);
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [inline]);

  // Hlasovky umí jen admin, poradce a manažer (backend: voice_notes.py
  // `_require_authenticated`). Ostatním se endpoint NEVOLÁ — jinak prohlížeč
  // zapíše do konzole červené 403 při každém otevření klientské složky.
  const smiHlasovky = ["admin", "advisor", "manager"].includes(user?.role);

  // Load usage when component mounts
  useEffect(() => {
    if (!user || !smiHlasovky) return;
    api.get("/api/voice-notes/usage/me").then((r) => setUsage(r.data)).catch(() => {});
  }, [user, smiHlasovky]);

  // Don't render for unauthenticated users or users without the right role
  if (!user || !smiHlasovky) return null;

  const startRecording = async () => {
    if (!supported) {
      toast.error("Tvůj prohlížeč nepodporuje nahrávání. Zkus Chrome.");
      return;
    }
    if (usage && usage.used >= usage.limit) {
      toast.error(`Měsíční limit ${usage.limit} nahrávek vyčerpán.`);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeOptions = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mime = mimeOptions.find((m) => MediaRecorder.isTypeSupported(m)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mediaRecorderRef.current = mr;
      mr.start(1000);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      setRecording(true);
    } catch (err) {
      toast.error("Nemohu přistoupit k mikrofonu. Povol přístup v prohlížeči.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setRecording(false);
  };

  const handleStop = async () => {
    setProcessing(true);
    try {
      const mime = mediaRecorderRef.current?.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mime });
      if (blob.size < 2000) {
        toast.error("Nahrávka je moc krátká. Zkus to znovu.");
        return;
      }
      const ext = mime.includes("mp4") ? "m4a" : "webm";
      const form = new FormData();
      form.append("audio", blob, `voice-note.${ext}`);
      // If recording is launched from within a client folder, force this contact_id
      // → backend skips fuzzy matching and saves the note + tasks + contract straight to this person.
      if (forcedContactId) {
        form.append("force_contact_id", forcedContactId);
      }
      const r = await api.post("/api/voice-notes/record", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(r.data);
      const taskCount = (r.data?.auto_created_task_ids || []).length;
      const matchInfo = r.data?.contact_match;
      // Smart matching toast — let advisor see what AI did with the client
      if (matchInfo?.action === "forced") {
        toast.success(`✓ Nahráno přímo ke klientovi ${forcedContactName || "(zvolený)"}.`);
      } else if (matchInfo?.action === "linked_existing") {
        const reasonLabel = matchInfo.reason === "phone_exact" ? "shoda telefonu"
          : matchInfo.reason === "email_exact" ? "shoda emailu"
          : `shoda jména ${matchInfo.score}%`;
        toast.success(`✓ Spojeno s existujícím klientem (${reasonLabel}) — vyhnuli jsme se duplicitě.`);
      } else if (matchInfo?.action === "created_new") {
        toast.success("✓ Vytvořen nový klient v CRM.");
      }
      if (taskCount > 0) {
        toast.success(`${taskCount} ${taskCount === 1 ? "úkol přidán" : taskCount < 5 ? "úkoly přidány" : "úkolů přidáno"} do tvého diáře.`);
      } else if (!matchInfo) {
        toast.success("Nahrávka zpracována.");
      }
      // Tell parent (e.g. ClientDetailRaynet) the upload finished — so it can refresh its data
      if (typeof onComplete === "function") {
        try { onComplete(r.data); } catch { /* no-op */ }
      }
      if (r.data?.auto_contract_id) {
        toast.success("Smlouva automaticky uložena + naplánovány lifecycle úkoly.");
      }
      // Refresh usage counter
      api.get("/api/voice-notes/usage/me").then((rr) => setUsage(rr.data)).catch(() => {});
      // Try to parse a contract from the transcript (silent — only shows card if confident)
      const transcript = r.data?.transcript;
      if (transcript && transcript.length > 30) {
        try {
          const cr = await api.post("/api/ai/voice-to-contract", { transcript });
          if (cr.data && cr.data.type && (cr.data.confidence == null || cr.data.confidence >= 0.4)) {
            setContractDraft(cr.data);
          }
        } catch (e) { /* silent — voice-to-contract is opportunistic */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Zpracování selhalo. Zkus to znovu.";
      toast.error(detail);
    } finally {
      setProcessing(false);
      chunksRef.current = [];
    }
  };

  const mmss = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
  const remaining = usage ? Math.max(0, usage.limit - usage.used) : null;

  return (
    <>
      {/* Floating mic button — bottom-left, above AI assistant (ai = bottom-6, this = bottom-24).
          Hides on scroll-down on mobile so it doesn't cover bottom rows of tables. */}
      {!inline && (
      <motion.div
        className="fixed bottom-24 right-6 z-50"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: hiddenOnScroll && !recording ? 0 : 1,
          y: hiddenOnScroll && !recording ? 80 : 0,
          scale: 1,
          pointerEvents: hiddenOnScroll && !recording ? "none" : "auto",
        }}
        transition={{ delay: 0.3, type: "spring", stiffness: 280, damping: 28 }}
      >
        <button
          onClick={recording ? stopRecording : startRecording}
          disabled={processing}
          data-testid="voice-recorder-btn"
          aria-label={recording ? "Zastavit nahrávání" : "Spustit hlasovou nahrávku"}
          className="relative w-14 h-14 rounded-full flex items-center justify-center transition-all"
          style={
            recording
              ? {
                  background: "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                  boxShadow: "0 12px 32px rgba(220,38,38,0.45), 0 0 0 6px rgba(220,38,38,0.10)",
                }
              : processing
                ? {
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    boxShadow: "0 12px 32px rgba(194,89,52,0.30)",
                    opacity: 0.7,
                    cursor: "wait",
                  }
                : {
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    boxShadow: "0 12px 32px rgba(194,89,52,0.40), inset 0 1px 0 rgba(255,255,255,0.18)",
                  }
          }
          onMouseEnter={(e) => {
            if (!recording && !processing) e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            if (!recording && !processing) e.currentTarget.style.transform = "scale(1)";
          }}
        >
          {processing ? (
            <Loader2 size={22} className="text-white animate-spin" />
          ) : recording ? (
            <span className="relative flex items-center justify-center w-6 h-6">
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  background: "var(--surface)",
                  animation: "voice-pulse 1.2s ease-out infinite",
                  opacity: 0.5,
                }}
              />
              <span className="relative w-3 h-3 rounded-sm" style={{ background: "var(--surface)" }} />
            </span>
          ) : (
            <Mic size={22} className="text-white" strokeWidth={2} />
          )}
          {recording && (
            <span
              className="absolute -top-1 -right-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full"
              style={{
                background: "var(--ink)",
                color: "var(--surface)",
                border: "2px solid #FDFBF7",
              }}
            >
              {mmss(elapsed)}
            </span>
          )}
          {remaining !== null && !recording && !processing && (
            <span
              className="absolute -bottom-1 -right-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full"
              style={{
                background: "var(--surface)",
                color: "var(--accent)",
                border: "2px solid #FDFBF7",
                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
              }}
            >
              {remaining}/{usage.limit}
            </span>
          )}
        </button>
      </motion.div>
      )}

      {/* Inline mode — rendered straight inside a parent panel (e.g. ClientFolderDrawer)
          with no fixed positioning. v3.9: cognac-branded, premium feel, pulsing dot
          when recording, waveform-feel animation. */}
      {inline && (
        <button
          onClick={recording ? stopRecording : startRecording}
          disabled={processing}
          data-testid="voice-recorder-inline-btn"
          aria-label={recording ? "Zastavit nahrávání" : "Spustit hlasovou nahrávku"}
          className="relative inline-flex items-center gap-2.5 px-4 py-2 rounded-full font-semibold transition-all overflow-hidden"
          style={
            recording
              ? {
                  background: "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                  color: "var(--surface)",
                  boxShadow: "0 4px 14px rgba(220,38,38,0.30), 0 0 0 4px rgba(220,38,38,0.10)",
                }
              : processing
                ? {
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    color: "var(--surface)",
                    opacity: 0.7,
                    cursor: "wait",
                    boxShadow: "0 4px 14px rgba(194,89,52,0.25)",
                  }
                : {
                    background: "linear-gradient(135deg, #C25934 0%, #4A3F35 100%)",
                    color: "var(--surface)",
                    boxShadow: "0 4px 14px rgba(194,89,52,0.30), inset 0 1px 0 rgba(255,255,255,0.18)",
                  }
          }
          onMouseEnter={(e) => {
            if (!recording && !processing) {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(194,89,52,0.40), inset 0 1px 0 rgba(255,255,255,0.18)";
            }
          }}
          onMouseLeave={(e) => {
            if (!recording && !processing) {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 14px rgba(194,89,52,0.30), inset 0 1px 0 rgba(255,255,255,0.18)";
            }
          }}
        >
          {/* Pulsing red dot while recording */}
          {recording && (
            <span className="relative flex items-center justify-center" style={{ width: 16, height: 16 }}>
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  background: "var(--surface)",
                  animation: "voice-pulse 1.2s ease-out infinite",
                  opacity: 0.6,
                }}
              />
              <span className="relative w-2 h-2 rounded-full" style={{ background: "var(--surface)" }} />
            </span>
          )}
          {!recording && (
            processing
              ? <Loader2 size={14} className="animate-spin" />
              : <Mic size={14} strokeWidth={2.2} />
          )}
          <span
            className="text-[12.5px] tracking-wide"
            style={{ fontWeight: 600, letterSpacing: "0.01em" }}
          >
            {processing
              ? "Zpracovávám…"
              : recording
                ? `Nahrávám · ${mmss(elapsed)} · Stop`
                : "Nahrát hlasovku"}
          </span>
        </button>
      )}

      {/* Approval Panel — Agent Concierge UX */}
      <AnimatePresence>
        {result && (
          <VoiceApprovalPanel
            result={result}
            onClose={() => setResult(null)}
            onApprove={(cid) => {
              if (cid) {
                window.dispatchEvent(new CustomEvent("open-client-folder", { detail: { contactId: cid } }));
              } else {
                navigate("/diar");
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Voice-to-Contract Preview Modal */}
      <AnimatePresence>
        {contractDraft && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[75] flex items-end justify-center px-4 pb-8 sm:items-center sm:pb-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setContractDraft(null)}
            data-testid="voice-contract-preview"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
              onClick={(e) => e.stopPropagation()}
              className="apple-skin w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
              style={{ background: "var(--bg)" }}
            >
              <div className="px-6 pt-6 pb-2" style={{ background: "var(--bg-soft)" }}>
                <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--accent)" }}>
                  <Sparkles size={13} />
                  <span style={{ fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    Detekována smlouva
                  </span>
                  {contractDraft.confidence != null && (
                    <span className="ml-auto text-[10px]" style={{ color: "var(--ink-muted)" }}>
                      {Math.round(contractDraft.confidence * 100)}% jistota
                    </span>
                  )}
                </div>
                <h3 className="serif text-2xl" style={{ color: "var(--ink)" }}>
                  Uložit k {contractDraft.contact_name_guess || "klientovi"}?
                </h3>
                {contractDraft.raw_quote && (
                  <p className="text-xs mt-1 italic" style={{ color: "var(--ink-muted)" }}>
                    „{contractDraft.raw_quote}“
                  </p>
                )}
              </div>
              <div className="px-6 py-4 space-y-2">
                {contractDraft.type && <DetailRow label="Typ" value={contractTypeLabel(contractDraft.type)} />}
                {contractDraft.provider && <DetailRow label="Banka / pojišťovna" value={contractDraft.provider} />}
                {contractDraft.fixation_end && <DetailRow label="Konec fixace" value={contractDraft.fixation_end} />}
                {contractDraft.end_date && <DetailRow label="Konec smlouvy" value={contractDraft.end_date} />}
                {contractDraft.start_date && <DetailRow label="Začátek" value={contractDraft.start_date} />}
                {contractDraft.monthly_amount != null && <DetailRow label="Měsíčně" value={`${contractDraft.monthly_amount.toLocaleString("cs-CZ")} Kč`} />}
                {contractDraft.rate != null && <DetailRow label="Sazba" value={`${contractDraft.rate}%`} />}
              </div>
              <div className="px-6 pb-6 pt-2 flex gap-2">
                <button
                  onClick={() => setContractDraft(null)}
                  className="btn-ghost flex-1"
                  data-testid="voice-contract-cancel"
                >
                  Zrušit
                </button>
                <button
                  onClick={async () => {
                    if (!contractDraft.contact_id) {
                      toast.error("Klient nenalezen — uložím ji ručně z klientské složky.");
                      setContractDraft(null);
                      return;
                    }
                    setSavingContract(true);
                    try {
                      const body = {
                        contact_id: contractDraft.contact_id,
                        type: contractDraft.type,
                        provider: contractDraft.provider,
                        fixation_end: contractDraft.fixation_end,
                        end_date: contractDraft.end_date,
                        start_date: contractDraft.start_date,
                        monthly_amount: contractDraft.monthly_amount,
                        rate: contractDraft.rate,
                        notes: contractDraft.notes,
                      };
                      Object.keys(body).forEach((k) => body[k] == null && delete body[k]);
                      await api.post("/api/contracts", body);
                      toast.success("Smlouva uložena ke klientovi.");
                      setContractDraft(null);
                    } catch (e) {
                      toast.error("Chyba při ukládání smlouvy.");
                    } finally {
                      setSavingContract(false);
                    }
                  }}
                  disabled={savingContract}
                  className="btn-cognac flex-1"
                  data-testid="voice-contract-save"
                >
                  {savingContract ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  Uložit smlouvu
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


/* ─────────────── Helpers for voice-to-contract preview card ─────────────── */
function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-[11px] uppercase tracking-wider" style={{ color: "var(--ink-muted)" }}>
        {label}
      </span>
      <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>
        {value}
      </span>
    </div>
  );
}

const CONTRACT_LABELS = {
  hypoteka: "Hypotéka",
  pojisteni_auta: "Pojištění auta",
  pojisteni_zivot: "Životní pojištění",
  pojisteni_majetek: "Pojištění majetku",
  penzijko: "Penzijko / DPS / DIP",
  investice: "Investice",
  uver: "Úvěr",
  stavebni: "Stavební spoření",
  zdravotni: "Zdravotní pojištění",
  jine: "Jiné",
  pojisteni_odpovednost: "Pojištění odpovědnosti",
  pojisteni_ostatni: "Ostatní pojištění",
};
function contractTypeLabel(t) {
  return CONTRACT_LABELS[t] || t;
}
